import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import Layout from './components/Common/Layout';
import LoginForm from './components/Auth/LoginForm';
import EmployeeList from './components/Employee/EmployeeList';
import EmployeeForm from './components/Employee/EmployeeForm';
import EmployeeEdit from './components/Employee/EmployeeEdit';
import LeaveList from './components/Leave/LeaveList';
import LeaveForm from './components/Leave/LeaveForm';
import HolidayList from './components/Holiday/HolidayList';
import HolidayForm from './components/Holiday/HolidayForm';
import ProjectList from './components/Project/ProjectList';
import ProjectForm from './components/Project/ProjectForm';
import ProjectEdit from './components/Project/ProjectEdit';
import LocationList from './components/Location/LocationList';
import LocationForm from './components/Location/LocationForm';
import Reports from './components/Reports/Reports';
import InvoiceSection from './components/Invoice/InvoiceSection';
import BatchUpload from './components/BatchUpload/BatchUpload';
import SBLView from './components/SBLView/SBLView';
import UserManagement from './components/User/UserManagement';
import TeamManagement from './components/User/TeamManagement';
import SOWReport from './components/SOWReport/SOWReport';
import ProjectionComparison from './components/ProjectionComparison/ProjectionComparison';

const theme = createTheme({
  palette: {
    primary: {
      main: '#5C6BC0',
      light: '#8E99F3',
      dark: '#26418F',
      contrastText: '#fff',
    },
    secondary: {
      main: '#26A69A',
      light: '#64D8CB',
      dark: '#00766C',
      contrastText: '#fff',
    },
    success: {
      main: '#66BB6A',
      light: '#98EE99',
      dark: '#338A3E',
    },
    warning: {
      main: '#FFA726',
      light: '#FFD95B',
      dark: '#C77800',
    },
    error: {
      main: '#EF5350',
      light: '#FF867C',
      dark: '#B61827',
    },
    info: {
      main: '#42A5F5',
      light: '#80D6FF',
      dark: '#0077C2',
    },
    background: {
      default: '#F5F7FA',
      paper: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          padding: '8px 20px',
        },
        contained: {
          boxShadow: '0 4px 14px 0 rgba(0,0,0,0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 12px 0 rgba(0,0,0,0.08)',
        },
        elevation1: {
          boxShadow: '0 2px 12px 0 rgba(0,0,0,0.08)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.08)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 700,
            backgroundColor: '#F8FAFC',
            color: '#475569',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: '#E2E8F0',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginForm />} />
            <Route path="/" element={<Layout />}>
              <Route index element={<Navigate to="/employees" replace />} />
              <Route path="employees" element={<EmployeeList />} />
              <Route path="employees/new" element={<EmployeeForm />} />
              <Route path="employees/edit/:id" element={<EmployeeEdit />} />
              <Route path="leaves" element={<LeaveList />} />
              <Route path="leaves/new" element={<LeaveForm />} />
              <Route path="holidays" element={<HolidayList />} />
              <Route path="holidays/new" element={<HolidayForm />} />
              <Route path="projects" element={<ProjectList />} />
              <Route path="projects/new" element={<ProjectForm />} />
              <Route path="projects/edit/:code" element={<ProjectEdit />} />
              <Route path="sow-report" element={<SOWReport />} />
              <Route path="projection-comparison" element={<ProjectionComparison />} />
              <Route path="locations" element={<LocationList />} />
              <Route path="locations/new" element={<LocationForm />} />
              <Route path="locations/edit/:id" element={<LocationForm />} />
              <Route path="reports" element={<Reports />} />
              <Route path="invoices" element={<InvoiceSection />} />
              <Route path="sbl-view" element={<SBLView />} />
              <Route path="batch-upload" element={<BatchUpload />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="my-team" element={<TeamManagement />} />
            </Route>
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
